import React, { useState, useImperativeHandle, forwardRef, useRef, useEffect } from "react";

import { KeyRound, Mail, Smartphone } from "lucide-react";

import { Button } from "js/components/ui/button";
import { cn } from "lib/utils";

import { CreditReport } from "js/components/ExpressReport/types";

import { CreditReportException } from "js/exceptions/CreditReportException";

import {
  useConfirmPinCreditReport,
  useResendPinCreditReport,
} from "../../../hooks/useCreditReports";
import { toast } from "../../../utils/toast";
import { getCreditReport } from "../../../api/creditReport";

interface pinConfirmInterface {
  email: string;
  creditReport: CreditReport;
  setCreditReport: (response: CreditReport) => void;
  onSubmittingChange?: (loading: boolean) => void;
}

export interface PinConfirmRef {
  triggerSubmit: () => void;
  getIsLoading: () => boolean;
  getRetryPin: () => number;
}

const PinConfirm = forwardRef<PinConfirmRef, pinConfirmInterface>(({
  email,
  creditReport,
  setCreditReport,
  onSubmittingChange,
}, ref) => {
  const [disclaimerCompany] = useState<string>("Multiburó SAPI de C.V.");
  const [retryPin, setRetryPin] = useState<number>(
    10 - (creditReport.pin_retry_attempts ?? 0),
  );
  const [resendPin, setResendPin] = useState<number>(
    5 - (creditReport.pin_resent_attempts ?? 1),
  );

  // Form state
  const [pinDigits, setPinDigits] = useState<string[]>(["", "", "", ""]);
  const [confirmPinDigits, setConfirmPinDigits] = useState<string[]>(["", "", "", ""]);
  const [error, setError] = useState<string>();
  const [confirmError, setConfirmError] = useState<string>();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const confirmInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Get combined PIN values
  const pin = pinDigits.join("");
  const confirmPin = confirmPinDigits.join("");

  // TanStack Query mutations
  const confirmPinMutation = useConfirmPinCreditReport();
  const resendPinMutation = useResendPinCreditReport();

  // Derive loading state from mutations
  const isLoading = confirmPinMutation.isPending || resendPinMutation.isPending;

  // Notify the parent page while the confirm-PIN request is in flight, so it can
  // show a loader + "Consultando tu reporte…" on the "Autorizar" button. We only
  // signal the confirm mutation (not resend) so the message reflects the actual
  // credit-bureau query.
  useEffect(() => {
    onSubmittingChange?.(confirmPinMutation.isPending);
  }, [confirmPinMutation.isPending, onSubmittingChange]);

  const handleDigitChange = (index: number, value: string) => {
    // Only allow single digit
    const digit = value.replace(/[^0-9]/g, "").slice(-1);

    const newDigits = [...pinDigits];
    newDigits[index] = digit;
    setPinDigits(newDigits);
    if (error) setError(undefined);
    if (confirmError) setConfirmError(undefined);

    // Auto-focus next input, or first confirm input if completing PIN
    if (digit && index < 3) {
      inputRefs.current[index + 1]?.focus();
    } else if (digit && index === 3) {
      // Move to first confirm input when PIN is complete
      confirmInputRefs.current[0]?.focus();
    }
  };

  const handleConfirmDigitChange = (index: number, value: string) => {
    // Only allow single digit
    const digit = value.replace(/[^0-9]/g, "").slice(-1);

    const newDigits = [...confirmPinDigits];
    newDigits[index] = digit;
    setConfirmPinDigits(newDigits);
    if (confirmError) setConfirmError(undefined);

    // Auto-focus next input
    if (digit && index < 3) {
      confirmInputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace - go to previous input
    if (e.key === "Backspace" && !pinDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleConfirmKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace - go to previous input or last PIN input
    if (e.key === "Backspace" && !confirmPinDigits[index]) {
      if (index > 0) {
        confirmInputRefs.current[index - 1]?.focus();
      } else {
        // Go back to last PIN input
        inputRefs.current[3]?.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, 4);
    const newDigits = [...pinDigits];
    for (let i = 0; i < pastedData.length; i++) {
      newDigits[i] = pastedData[i];
    }
    setPinDigits(newDigits);
    // Focus the appropriate input or move to confirm
    if (pastedData.length === 4) {
      confirmInputRefs.current[0]?.focus();
    } else {
      const focusIndex = Math.min(pastedData.length, 3);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleConfirmPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, 4);
    const newDigits = [...confirmPinDigits];
    for (let i = 0; i < pastedData.length; i++) {
      newDigits[i] = pastedData[i];
    }
    setConfirmPinDigits(newDigits);
    // Focus the appropriate input
    const focusIndex = Math.min(pastedData.length, 3);
    confirmInputRefs.current[focusIndex]?.focus();
  };

  const validateForm = (): boolean => {
    let isValid = true;

    if (!pin) {
      setError("Por favor introduzca su NIP");
      isValid = false;
    } else if (pin.length !== 4) {
      setError("El NIP debe tener 4 dígitos");
      isValid = false;
    }

    if (!confirmPin) {
      setConfirmError("Por favor confirme su NIP");
      isValid = false;
    } else if (confirmPin.length !== 4) {
      setConfirmError("La confirmación debe tener 4 dígitos");
      isValid = false;
    } else if (pin && confirmPin && pin !== confirmPin) {
      setConfirmError("Los NIP no coinciden");
      isValid = false;
    }

    return isValid;
  };

  const resendPinByMethod = (method: string) => {
    // Optimistic update: decrease counter before mutation
    setResendPin((prev) => Math.max(prev - 1, 0));

    resendPinMutation.mutate(
      { reportId: creditReport.id, method },
      {
        onSuccess: () => {
          // Show success toast
          toast.success(`Reenviamos tu NIP por ${method}`);
          // Note: resend-pin endpoint returns { code, message }, NOT a CreditReport
          // The pin_resent_attempts is updated server-side but we don't need to
          // refetch since the local counter is already updated optimistically
        },
        onError: (error: any) => {
          // Rollback optimistic update on error
          setResendPin((prev) => Math.max(prev + 1, 0));
          const errorMessage =
            error?.response?.data?.message || "Error al reenviar el NIP";
          toast.error(errorMessage);
        },
      },
    );
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    confirmPinMutation.mutate(
      {
        reportId: creditReport.id,
        pin: pin,
        confirmPin: confirmPin,
      },
      {
        onSuccess: async (response) => {
          // Check if response contains an error code (API returns 200 OK with error body)
          if (response && response.code) {
            // Fetch fresh credit report to get actual state (don't use invalid response)
            try {
              const updatedCreditReport = await getCreditReport(creditReport.id);
              setCreditReport(updatedCreditReport);
            } catch {
              // If fetch fails, don't update state with invalid response
            }
            return;
          }

          // Update parent with valid credit report response
          setCreditReport(response);
        },
        onError: async (error: any) => {
          if (
            error.response &&
            error.response.data &&
            error.response.data.code
          ) {
            const errorCode = error.response.data.code;

            const creditReportException = new CreditReportException(errorCode);
            toast.error(creditReportException.message);

            if (errorCode === "1009") {
              setRetryPin((prev) => Math.max(prev - 1, 0));
            }

            // Always fetch fresh credit report to ensure counters are in sync (matches original behavior)
            try {
              const updatedCreditReport = await getCreditReport(creditReport.id);
              setCreditReport(updatedCreditReport);
            } catch (fetchError) {
              // Fallback to error response if fetch fails
              if (error.response.data.credit_report) {
                setCreditReport(error.response.data.credit_report);
              }
            }
          } else {
            toast.error(`Error: ${error.message || error}`);
          }
        },
      },
    );
  };

  // Expose submit function and state to parent component
  useImperativeHandle(ref, () => ({
    triggerSubmit: handleSubmit,
    getIsLoading: () => isLoading,
    getRetryPin: () => retryPin,
  }));

  return (
    <div id="pin-confirm" className="space-y-5">
      {/* Title and description */}
      <div className="space-y-2 text-center">
        <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-blue-50">
          <KeyRound className="h-5 w-5 text-blue-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900">
          Confirmar NIP
        </h2>
        <p className="text-sm text-gray-500">
          Introduce el Número de Identificación Personal (NIP) de 4
          dígitos que te enviamos a{" "}
          <span className="font-medium text-gray-900">{email}</span>
        </p>
      </div>

      {/* PIN Input Fields */}
      <div className="mx-auto max-w-xs space-y-4">
        {/* NIP Input */}
        <div className="space-y-2">
          <label className="block text-center text-sm font-medium text-gray-700">
            NIP
          </label>
          <div className="flex justify-center gap-3" onPaste={handlePaste}>
            {[0, 1, 2, 3].map((index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={pinDigits[index]}
                onChange={(e) => handleDigitChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                disabled={isLoading}
                className={cn(
                  "h-12 w-12 rounded-lg border-2 bg-white text-center text-xl font-semibold text-gray-900 outline-none transition-all",
                  "focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20",
                  pinDigits[index] ? "border-gray-300" : "border-gray-200",
                  error && "border-red-400 focus:border-red-500 focus:ring-red-500/20"
                )}
                autoComplete="one-time-code"
              />
            ))}
          </div>
          {error && (
            <p className="text-center text-xs text-red-600">{error}</p>
          )}
        </div>

        {/* Confirm NIP Input */}
        <div className="space-y-2">
          <label className="block text-center text-sm font-medium text-gray-700">
            Confirmar NIP
          </label>
          <div className="flex justify-center gap-3" onPaste={handleConfirmPaste}>
            {[0, 1, 2, 3].map((index) => (
              <input
                key={`confirm-${index}`}
                ref={(el) => (confirmInputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={confirmPinDigits[index]}
                onChange={(e) => handleConfirmDigitChange(index, e.target.value)}
                onKeyDown={(e) => handleConfirmKeyDown(index, e)}
                disabled={isLoading}
                className={cn(
                  "h-12 w-12 rounded-lg border-2 bg-white text-center text-xl font-semibold text-gray-900 outline-none transition-all",
                  "focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20",
                  confirmPinDigits[index] ? "border-gray-300" : "border-gray-200",
                  confirmError && "border-red-400 focus:border-red-500 focus:ring-red-500/20"
                )}
                autoComplete="off"
              />
            ))}
          </div>
          {confirmError && (
            <p className="text-center text-xs text-red-600">{confirmError}</p>
          )}
        </div>
      </div>

      {/* Disclaimer */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <p className="text-xs leading-relaxed text-gray-600">
          Por este conducto autorizo expresamente a{" "}
          <span className="font-semibold">{disclaimerCompany}</span>, para que
          por conducto de sus funcionarios facultados lleve a cabo
          Investigaciones, sobre mi comportamiento Crediticio en las Sociedades
          de Información Crediticia que estime conveniente. Así mismo, declaro
          que conozco la naturaleza y alcance de la información que se
          solicitará, del uso que{" "}
          <span className="font-semibold">{disclaimerCompany}</span> hará de tal
          información y de que ésta podrá realizar consultas periódicas de mi
          historial crediticio, consintiendo que esta autorización se encuentre
          vigente por un periodo de 3 años contados a partir de la fecha de su
          expedición y en todo caso durante el tiempo que mantengamos relación
          jurídica. Acepto que este documento quede bajo propiedad de{" "}
          <span className="font-semibold">{disclaimerCompany}</span> y/o en las
          sociedades de información crediticia consultadas para efectos de
          control y cumplimiento del artículo 28 de la LRSIC.
        </p>
      </div>

      {/* Resend buttons */}
      <div className="mx-auto max-w-sm space-y-2">
        <Button
          variant="ghost"
          size="sm"
          disabled={resendPin === 0 || isLoading}
          onClick={() => resendPinByMethod("Email")}
          className="w-full justify-center text-gray-600 hover:text-blue-600 hover:bg-blue-50"
        >
          <Mail className="mr-2 h-4 w-4" />
          Reenviar NIP via Email ({resendPin})
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={resendPin === 0 || isLoading}
          onClick={() => resendPinByMethod("SMS")}
          className="w-full justify-center text-gray-600 hover:text-blue-600 hover:bg-blue-50"
        >
          <Smartphone className="mr-2 h-4 w-4" />
          Reenviar NIP via SMS ({resendPin})
        </Button>
      </div>
    </div>
  );
});

PinConfirm.displayName = "PinConfirm";

export default PinConfirm;
